import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, IconButton, Paper, Skeleton } from "@mui/material";
import * as Mui from "@mui/material";
import {
  Close,
  CloseFullscreen,
} from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm, Controller } from "react-hook-form";
import { parentBoxStyle, themes } from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import dayjs from "dayjs";
import { useFetch } from "services/hooks/useFetch";
import {
  APP_BAR_OFFSET,
  designColors,
  designRadii,
  designShell,
  designType,
  FORM_ACTION_BUTTON_WIDTH,
  PANEL_DRAWER_WIDTH,
  panelDrawerSx,
} from "services/constants/designTokens";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  GlobalFieldLabel,
  useExpandedContentLeft,
} from "components/DesignSystem/sharedUiPatterns";
import { SelectedEmployeeHeader } from "../SelectedEmployeeHeader";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";

// Read-only Start/End/Duration list, used for both the "Existing Shift
// Breaks" and "Existing OT Breaks" context tables. Ported verbatim from
// CreateWorkAdjustment.js's renderBreakEntries.
const renderBreakEntries = (breaks, emptyMessage) => {
  if (!breaks?.length) {
    return (
      <Typography sx={{ fontSize: "0.9rem", color: designColors.slate600 }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.75}>
      <Mui.Grid container spacing={0.75} sx={{ px: 0.75 }}>
        <Mui.Grid item xs={4}>
          <Typography variant="caption" color={designColors.slate600}>
            Start
          </Typography>
        </Mui.Grid>
        <Mui.Grid item xs={4}>
          <Typography variant="caption" color={designColors.slate600}>
            End
          </Typography>
        </Mui.Grid>
        <Mui.Grid item xs={4}>
          <Typography variant="caption" color={designColors.slate600}>
            Duration
          </Typography>
        </Mui.Grid>
      </Mui.Grid>
      {breaks.map((item, index) => (
        <Box
          key={
            item?.id ||
            `${item?.start_time || item?.break_start_time || "break"}_${index}`
          }
          sx={{
            p: 0.75,
            borderRadius: 1,
            backgroundColor: "#ffffff",
            border: `1px solid ${themes?.miliedLightGray}`,
          }}
        >
          <Mui.Grid container spacing={0.75}>
            <Mui.Grid item xs={4}>
              <Typography sx={{ lineHeight: 1.3 }}>
                {item?.start_time || item?.break_start_time
                  ? dayjs(item?.start_time || item?.break_start_time).format(
                      "HH:mm",
                    )
                  : "-"}
              </Typography>
            </Mui.Grid>
            <Mui.Grid item xs={4}>
              <Typography sx={{ lineHeight: 1.3 }}>
                {item?.end_time || item?.break_end_time
                  ? dayjs(item?.end_time || item?.break_end_time).format(
                      "HH:mm",
                    )
                  : "-"}
              </Typography>
            </Mui.Grid>
            <Mui.Grid item xs={4}>
              <Typography sx={{ lineHeight: 1.3 }}>
                {item?.duration || "-"}
              </Typography>
            </Mui.Grid>
          </Mui.Grid>
        </Box>
      ))}
    </Box>
  );
};

export const Apply = ({
  onClose,
  setReCallApi,
  open = true,
  expanded = false,
  onToggleExpand,
}) => {
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      date: null,
      requestMode: "cancel",
      break_category: "OT Break",
      startTime: null,
      endTime: null,
      break_to_cancel: [],
      reason: "",
      button_loader: false,
    },
  });
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);
  const { domain, userDetails, userScreenAccess, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );
  // See Leave/Apply.js for the full explanation — this used to always post
  // status:"Approved" regardless of the creator's actual approver
  // permission, so any editor auto-approved their own break creation.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Break",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Break",
    );
  const requestMode = watch("requestMode");
  // Cancelling a break needs a different employee pool than applying one, so
  // the "type" filter switches with the Request Type toggle above.
  const { result: users, setLoad: reloadUsers } = useFetch(
    `${domain}team_zone/approve/work_adjustment/users-by-type/?user_id=${userDetails.id}&type=${encodeURIComponent(
      requestMode === "cancel" ? "Cancel Break" : "Break",
    )}&company_id=${topbarCompanyId || ""}`,
  );
  const contentLeft = useExpandedContentLeft(expanded);

  useEffect(() => {
    if (open) {
      reset();
      setShowAutoCompleteAvathar(false);
    }
  }, [open, reset]);

  const selectedUserId = watch("user_id");
  const attendanceDate = watch("date");
  const breakCategory = watch("break_category");
  const breakStartTime = watch("startTime");
  const breakEndTime = watch("endTime");
  const isValidAttendanceDate =
    attendanceDate && dayjs(attendanceDate).isValid();
  const shouldFetchBreakData =
    selectedUserId && isValidAttendanceDate && requestMode !== "cancel";
  const shouldFetchCancelBreakOptions =
    selectedUserId && isValidAttendanceDate && requestMode === "cancel";

  const { result: initialBreakData } = useFetch(
    selectedUserId
      ? `${domain}team_zone/approve/work_adjustment/user-break-context/?user_id=${selectedUserId}&company_id=${topbarCompanyId}`
      : null,
  );
  const { result: breakData, setLoad: reloadBreakData } = useFetch(
    shouldFetchBreakData
      ? `${domain}team_zone/approve/work_adjustment/user-break-context/?user_id=${selectedUserId}&date=${dayjs(
          attendanceDate,
        ).format("YYYY-MM-DD")}&company_id=${topbarCompanyId}`
      : null,
  );
  const {
    result: cancelBreakApiData,
    setLoad: reloadCancelBreakOptions,
    loading: cancelBreakLoading,
  } =
    useFetch(
      shouldFetchCancelBreakOptions
        ? `${domain}my_zone/apply/work_adjustment/cancel-break-options/?user_id=${selectedUserId}&date=${dayjs(
            attendanceDate,
          ).format("YYYY-MM-DD")}&company_id=${topbarCompanyId}`
        : null,
    );

  const shiftDetails = breakData?.date_stats?.shift_details;
  const shiftStartTime = breakData?.date_stats?.shift_details?.shift_start_time;
  const shiftEndTime = breakData?.date_stats?.shift_details?.shift_end_time;
  const isOvernightShift = Boolean(shiftDetails?.is_overnight_shift);
  const getTimeInMinutes = (timeValue) =>
    dayjs(timeValue).hour() * 60 + dayjs(timeValue).minute();
  const shiftStartMinutes = shiftStartTime
    ? getTimeInMinutes(dayjs(shiftStartTime, "HH:mm"))
    : null;
  const shiftEndMinutes = shiftEndTime
    ? getTimeInMinutes(dayjs(shiftEndTime, "HH:mm"))
    : null;
  const isTimeWithinShiftWindow = (timeValue, view = "minutes") => {
    if (
      shiftStartMinutes === null ||
      shiftEndMinutes === null ||
      !timeValue ||
      !dayjs(timeValue).isValid()
    ) {
      return false;
    }

    const candidateMinutes = getTimeInMinutes(dayjs(timeValue));

    if (view === "hours") {
      const hourStartMinutes = dayjs(timeValue).hour() * 60;
      const hourEndMinutes = hourStartMinutes + 59;

      return isOvernightShift
        ? hourEndMinutes >= shiftStartMinutes ||
            hourStartMinutes <= shiftEndMinutes
        : hourEndMinutes >= shiftStartMinutes &&
            hourStartMinutes <= shiftEndMinutes;
    }

    return isOvernightShift
      ? candidateMinutes >= shiftStartMinutes ||
          candidateMinutes <= shiftEndMinutes
      : candidateMinutes >= shiftStartMinutes &&
          candidateMinutes <= shiftEndMinutes;
  };
  const shiftMinTime =
    requestMode === "apply" &&
    breakCategory === "Shift Break" &&
    shiftStartTime &&
    !isOvernightShift
      ? dayjs(shiftStartTime, "HH:mm")
      : null;
  const shiftMaxTime =
    requestMode === "apply" &&
    breakCategory === "Shift Break" &&
    shiftEndTime &&
    !isOvernightShift
      ? dayjs(shiftEndTime, "HH:mm")
      : null;
  const shiftDurationInMinutes =
    shiftStartTime && shiftEndTime
      ? isOvernightShift
        ? 24 * 60 - shiftStartMinutes + shiftEndMinutes
        : shiftEndMinutes - shiftStartMinutes
      : null;
  const otRecords = breakData?.date_stats?.ot_records || [];
  const sortOtRecordsByStartTime = (items = []) =>
    [...items].sort((a, b) => {
      const first = dayjs(a?.ot_start_datetime);
      const second = dayjs(b?.ot_start_datetime);

      if (!first.isValid() || !second.isValid()) {
        return 0;
      }

      return first.valueOf() - second.valueOf();
    });
  const sortedOtRecords = sortOtRecordsByStartTime(otRecords);

  const disableBreakTime = (value, view) => {
    if (requestMode !== "apply" || !shiftStartTime || !shiftEndTime) {
      return false;
    }

    const isWithinShiftWindow = isTimeWithinShiftWindow(value, view);

    if (breakCategory === "Shift Break") {
      return !isWithinShiftWindow;
    }

    if (breakCategory === "OT Break") {
      if (view === "hours") {
        const hourStartMinutes = dayjs(value).hour() * 60;
        const hourEndMinutes = hourStartMinutes + 59;
        const hasTimeOutsideShift = isOvernightShift
          ? hourStartMinutes < shiftStartMinutes &&
            hourEndMinutes > shiftEndMinutes
          : hourStartMinutes < shiftStartMinutes ||
            hourEndMinutes > shiftEndMinutes;

        return !hasTimeOutsideShift;
      }

      return isWithinShiftWindow;
    }

    return false;
  };

  const getBreakDateTime = (timeValue) => {
    if (
      !attendanceDate ||
      !dayjs(attendanceDate).isValid() ||
      !timeValue ||
      !dayjs(timeValue).isValid()
    ) {
      return null;
    }

    const breakDateTime = dayjs(attendanceDate)
      .hour(dayjs(timeValue).hour())
      .minute(dayjs(timeValue).minute())
      .second(0);
    const nextDayBreakDateTime = breakDateTime.add(1, "day");

    if (breakCategory === "OT Break" && otRecords.length) {
      const matchingOtRecord = sortedOtRecords.find((record) => {
        const otStart = dayjs(record?.ot_start_datetime);
        const otEnd = dayjs(record?.ot_end_datetime);

        if (!otStart.isValid() || !otEnd.isValid()) {
          return false;
        }

        return (
          ((breakDateTime.isSame(otStart) || breakDateTime.isAfter(otStart)) &&
            (breakDateTime.isSame(otEnd) || breakDateTime.isBefore(otEnd))) ||
          ((nextDayBreakDateTime.isSame(otStart) ||
            nextDayBreakDateTime.isAfter(otStart)) &&
            (nextDayBreakDateTime.isSame(otEnd) ||
              nextDayBreakDateTime.isBefore(otEnd)))
        );
      });

      if (matchingOtRecord) {
        const otStart = dayjs(matchingOtRecord?.ot_start_datetime);
        const otEnd = dayjs(matchingOtRecord?.ot_end_datetime);

        if (
          (nextDayBreakDateTime.isSame(otStart) ||
            nextDayBreakDateTime.isAfter(otStart)) &&
          (nextDayBreakDateTime.isSame(otEnd) ||
            nextDayBreakDateTime.isBefore(otEnd))
        ) {
          return nextDayBreakDateTime;
        }

        return breakDateTime;
      }
    }

    if (
      isOvernightShift &&
      shiftEndMinutes !== null &&
      getTimeInMinutes(dayjs(timeValue)) <= shiftEndMinutes
    ) {
      return breakDateTime.add(1, "day");
    }

    return breakDateTime;
  };

  const selectedBreakStartDateTime = getBreakDateTime(breakStartTime);
  const selectedBreakEndDateTime = getBreakDateTime(breakEndTime);
  const selectedBreakDuration =
    selectedBreakStartDateTime &&
    selectedBreakEndDateTime &&
    selectedBreakEndDateTime.isAfter(selectedBreakStartDateTime)
      ? `${selectedBreakEndDateTime.diff(
          selectedBreakStartDateTime,
          "minute",
        )} minutes`
      : initialBreakData?.manual_break_duration
        ? `${initialBreakData.manual_break_duration} minutes`
        : "-";
  const selectedBreakDurationMinutes =
    selectedBreakStartDateTime &&
    selectedBreakEndDateTime &&
    selectedBreakEndDateTime.isAfter(selectedBreakStartDateTime)
      ? selectedBreakEndDateTime.diff(selectedBreakStartDateTime, "minute")
      : initialBreakData?.manual_break_duration || 0;
  const configuredBreakDurationMinutes =
    breakData?.manual_break_duration ??
    initialBreakData?.manual_break_duration ??
    0;

  const manualShiftBreaks = [
    ...(breakData?.date_stats?.manual_breaks_pending || []),
    ...(breakData?.date_stats?.manual_breaks_approved || []),
  ].filter(
    (item) => String(item?.break_type || "").toLowerCase() === "shift_break",
  );
  const existingShiftBreaks = [
    ...(breakData?.date_stats?.real_punch_breaks || []),
    ...manualShiftBreaks,
  ];
  const isAttendanceValid = breakData?.date_stats?.is_attendance_valid;
  const isOtAllowed =
    breakData?.date_stats?.is_ot_allowed ?? breakData?.is_ot_allowed;
  const applyDisabledReason =
    requestMode === "apply" && selectedUserId && isValidAttendanceDate
      ? isAttendanceValid === false
        ? "No attendance record found on the selected date"
        : breakCategory === "OT Break" && isOtAllowed === false
          ? "Not eligible to apply OT break on the selected date"
          : ""
      : "";
  const manualOtBreaks = [
    ...(breakData?.date_stats?.manual_breaks_pending || []),
    ...(breakData?.date_stats?.manual_breaks_approved || []),
  ].filter(
    (item) => String(item?.break_type || "").toLowerCase() === "ot_break",
  );
  const existingOtBreaks = [
    ...otRecords.flatMap((record) => record?.ot_breaks || []),
    ...manualOtBreaks,
  ].filter(
    (item, index, arr) =>
      arr.findIndex(
        (other) =>
          (other?.start_time || other?.break_start_time) ===
            (item?.start_time || item?.break_start_time) &&
          (other?.end_time || other?.break_end_time) ===
            (item?.end_time || item?.break_end_time),
      ) === index,
  );
  const sortBreakItemsByStartTime = (items = []) =>
    [...items].sort((a, b) => {
      const first = dayjs(a?.start_time || a?.break_start_time);
      const second = dayjs(b?.start_time || b?.break_start_time);

      if (!first.isValid() || !second.isValid()) {
        return 0;
      }

      return first.valueOf() - second.valueOf();
    });
  const sortedShiftBreaks = sortBreakItemsByStartTime(existingShiftBreaks);
  const sortedOtBreaks = sortBreakItemsByStartTime(existingOtBreaks);
  const cancelBreakApiList = Array.isArray(cancelBreakApiData)
    ? cancelBreakApiData
    : Array.isArray(cancelBreakApiData?.break_options)
      ? cancelBreakApiData.break_options
      : [];
  const resolveCancelBreakCategory = (item) => {
    const label = item?.category_label || item?.type_label;
    if (label === "Shift Break" || label === "OT Break") {
      return {
        code: label === "Shift Break" ? "Shift_break" : "Ot_break",
        label,
      };
    }

    const code = String(
      item?.category_code || item?.break_type || "",
    ).toLowerCase();
    const isOt = ["ot_break", "ot break"].includes(code) || item?.over_time_id;
    const isShift = !isOt;
    return {
      code: isShift ? "Shift_break" : "Ot_break",
      label: isShift ? "Shift Break" : "OT Break",
    };
  };
  const cancelBreakOptions = cancelBreakApiList.map((item, index) => {
    const category = resolveCancelBreakCategory(item);
    return {
      ...item,
      cancel_row_id: item?.break_id ?? item?.id ?? index,
      category_code: category.code,
      category_label: category.label,
    };
  });

  const validateOtBreakRange = (currentStart, currentEnd) => {
    if (!currentStart || !currentEnd) {
      return null;
    }

    const fallsWithinOtWindow = otRecords.some((record) => {
      const otStart = dayjs(record?.ot_start_datetime);
      const otEnd = dayjs(record?.ot_end_datetime);

      if (!otStart.isValid() || !otEnd.isValid()) {
        return false;
      }

      return (
        (currentStart.isSame(otStart) || currentStart.isAfter(otStart)) &&
        (currentEnd.isSame(otEnd) || currentEnd.isBefore(otEnd))
      );
    });

    if (!fallsWithinOtWindow) {
      return "Selected OT break must fall within a valid OT window";
    }

    const hasOtBreakOverlap = existingOtBreaks.some((item) => {
      const existingStart = dayjs(item?.start_time);
      const existingEnd = dayjs(item?.end_time);

      if (!existingStart.isValid() || !existingEnd.isValid()) {
        return false;
      }

      return (
        currentStart.isBefore(existingEnd) && currentEnd.isAfter(existingStart)
      );
    });

    if (hasOtBreakOverlap) {
      return "Selected OT break overlaps with an existing OT break";
    }

    return null;
  };

  const previousBreakCategoryRef = useRef();
  const previousRequestModeRef = useRef();

  useEffect(() => {
    if (selectedUserId && isValidAttendanceDate && requestMode === "cancel") {
      reloadBreakData(true);
      reloadCancelBreakOptions(true);
    }
  }, [
    selectedUserId,
    attendanceDate,
    isValidAttendanceDate,
    requestMode,
    reloadBreakData,
    reloadCancelBreakOptions,
  ]);

  // useFetch only fetches once on mount by itself — changing the URL alone
  // doesn't re-arm it, so switching Request Type needs an explicit reload to
  // pick up the other type's employee list.
  useEffect(() => {
    reloadUsers(true);
  }, [requestMode, reloadUsers]);

  useEffect(() => {
    const previousBreakCategory = previousBreakCategoryRef.current;
    const previousRequestMode = previousRequestModeRef.current;
    const hasSwitchedToOtBreak =
      previousBreakCategory !== undefined &&
      previousBreakCategory !== "OT Break" &&
      breakCategory === "OT Break";
    const hasSwitchedToShiftBreak =
      previousBreakCategory !== undefined &&
      previousBreakCategory !== "Shift Break" &&
      breakCategory === "Shift Break";
    const hasEnteredApplyWithOtCategory =
      previousRequestMode !== undefined &&
      previousRequestMode !== "apply" &&
      requestMode === "apply" &&
      breakCategory === "OT Break";

    previousBreakCategoryRef.current = breakCategory;
    previousRequestModeRef.current = requestMode;

    if (hasSwitchedToOtBreak || hasSwitchedToShiftBreak) {
      setValue("startTime", null);
      setValue("endTime", null);
      clearErrors(["startTime", "endTime"]);
    }

    if (
      requestMode !== "apply" ||
      breakCategory !== "OT Break" ||
      !sortedOtRecords.length
    ) {
      return;
    }

    if (
      !hasSwitchedToOtBreak &&
      !hasEnteredApplyWithOtCategory &&
      breakStartTime &&
      breakEndTime
    ) {
      return;
    }

    const defaultOtRecord = sortedOtRecords[sortedOtRecords.length - 1];
    const defaultStartTime = dayjs(defaultOtRecord?.ot_start_datetime);

    if (!defaultStartTime.isValid()) {
      return;
    }

    setValue("startTime", defaultStartTime);
    setValue(
      "endTime",
      defaultStartTime.add(configuredBreakDurationMinutes, "minute"),
    );
  }, [
    requestMode,
    breakCategory,
    breakStartTime,
    breakEndTime,
    configuredBreakDurationMinutes,
    clearErrors,
    setValue,
    sortedOtRecords,
  ]);

  useEffect(() => {
    if (requestMode !== "cancel") {
      setValue("break_to_cancel", []);
    }
  }, [requestMode, setValue]);

  const updateUserName = (event, data) => {
    if (data) {
      setValue("user_name", data?.label);
      setValue("user_id", data?.user_id);
      setValue("selectedUser", data);
      setShowAutoCompleteAvathar(false);
    } else {
      setValue("user_name", null);
      setValue("user_id", "");
      setValue("selectedUser", null);
      setShowAutoCompleteAvathar(true);
    }
  };

  const BreakContextInfo = () => {
    if (requestMode !== "apply") {
      return null;
    }

    if (breakCategory === "Shift Break") {
      return (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 1.5,
            border: `1px solid ${themes?.miliedLightGray}`,
            backgroundColor: "#f8fafc",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", mb: 1.25 }}>
            Shift Details
          </Typography>
          <Mui.Grid container spacing={1.5}>
            <Mui.Grid item xs={12} sm={4}>
              <Typography
                sx={{ fontSize: "0.9rem", color: designColors.slate600, mb: 0.5 }}
              >
                Shift Name
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {shiftDetails?.shift_name || "-"}
              </Typography>
            </Mui.Grid>
            <Mui.Grid item xs={12} sm={4}>
              <Typography
                sx={{ fontSize: "0.9rem", color: designColors.slate600, mb: 0.5 }}
              >
                Shift Start
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {shiftDetails?.shift_start_time || "-"}
              </Typography>
            </Mui.Grid>
            <Mui.Grid item xs={12} sm={4}>
              <Typography
                sx={{ fontSize: "0.9rem", color: designColors.slate600, mb: 0.5 }}
              >
                Shift End
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {shiftDetails?.shift_end_time || "-"}
              </Typography>
            </Mui.Grid>
          </Mui.Grid>
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 600, mb: 0.75 }}>
              Existing Shift Breaks
            </Typography>
            {renderBreakEntries(sortedShiftBreaks, "No shift breaks found")}
          </Box>
        </Paper>
      );
    }

    if (breakCategory === "OT Break") {
      if (!otRecords?.length) return null;

      return (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 1.5,
            border: `1px solid ${themes?.miliedLightGray}`,
            backgroundColor: "#fffaf0",
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 1 }}>OT Details</Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <Mui.Grid container spacing={0.75} sx={{ px: 0.75 }}>
              <Mui.Grid item xs={4}>
                <Typography variant="caption" color={designColors.slate600}>
                  OT Start
                </Typography>
              </Mui.Grid>
              <Mui.Grid item xs={4}>
                <Typography variant="caption" color={designColors.slate600}>
                  OT End
                </Typography>
              </Mui.Grid>
              <Mui.Grid item xs={4}>
                <Typography variant="caption" color={designColors.slate600}>
                  Break Taken
                </Typography>
              </Mui.Grid>
            </Mui.Grid>
            {sortedOtRecords.map((record, index) => (
              <Box
                key={record?.ot_id || index}
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  backgroundColor: "#ffffff",
                  border: `1px solid ${themes?.miliedLightGray}`,
                }}
              >
                <Mui.Grid container spacing={0.75}>
                  <Mui.Grid item xs={4}>
                    <Typography sx={{ lineHeight: 1.3 }}>
                      {record?.ot_start_datetime
                        ? dayjs(record.ot_start_datetime).format("HH:mm")
                        : "-"}
                    </Typography>
                  </Mui.Grid>
                  <Mui.Grid item xs={4}>
                    <Typography sx={{ lineHeight: 1.3 }}>
                      {record?.ot_end_datetime
                        ? dayjs(record.ot_end_datetime).format("HH:mm")
                        : "-"}
                    </Typography>
                  </Mui.Grid>
                  <Mui.Grid item xs={4}>
                    <Typography sx={{ lineHeight: 1.3 }}>
                      {record?.total_ot_break_taken || "00:00"}
                    </Typography>
                  </Mui.Grid>
                </Mui.Grid>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 600, mb: 0.75 }}>
              Existing OT Breaks
            </Typography>
            {renderBreakEntries(sortedOtBreaks, "No OT breaks found")}
          </Box>
        </Paper>
      );
    }

    return null;
  };

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    let payload;
    if (data.requestMode === "cancel") {
      payload = {
        attendance_type: "Cancel Break",
        ...(!hasApproverAccess && { status: "Pending" }),
        user_id: data.user_id,
        break_ids: (data.break_to_cancel || []).map((entry) => entry.id),
        reason: data.reason,
      };
    } else {
      payload = {
        user_id: data.user_id,
        attendance_type: "break",
        reason: data.reason,
        status: hasApproverAccess ? "Approved" : "Pending",
        break_type: breakCategory === "OT Break" ? "Ot_break" : "Shift_break",
        break_start_time: getBreakDateTime(data.startTime)?.format(
          "YYYY-MM-DDTHH:mm:ssZ",
        ),
        break_end_time: getBreakDateTime(data.endTime)?.format(
          "YYYY-MM-DDTHH:mm:ssZ",
        ),
        duration: selectedBreakDurationMinutes,
        date: data.date?.format("YYYY-MM-DD"),
      };
    }
    const response = await customAPI(
      `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?company_id=${topbarCompanyId}`,
      "POST",
      payload,
    );
    if (response?.ok) {
      showToast(
        "success",
        data.requestMode === "cancel"
          ? "Cancel break request created."
          : "Break request created.",
      );
      setReCallApi(true);
      reset();
      onClose();
      setValue("button_loader", false);
    } else {
      setValue("button_loader", false);
      showToast(
        "error",
        response?.message || "Couldn't create the request. Please try again.",
      );
    }
  };

  const panelContent = (
    <Box
      sx={{
        ...parentBoxStyle,
        height: "100%",
        borderRadius: expanded ? designRadii.card : 0,
        overflow: "hidden",
        backgroundColor: designColors.white,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          backgroundColor: designColors.white,
        }}
      >
        <Box sx={{ p: 3, pb: 0 }}>
          <Mui.Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{
              pb: 2.25,
              borderBottom: `1px solid ${designColors.slate200}`,
            }}
          >
            <Box>
              <Typography sx={designType.panelTitle}>
                New Break Request
              </Typography>
              <Typography sx={{ ...designType.pageSubtitle, mt: 0.5 }}>
                Apply for a break or cancel an existing break for the selected date.
              </Typography>
            </Box>
            <Mui.Stack direction="row" spacing={1} alignItems="center">
              {onToggleExpand ? (
                expanded ? (
                  <PanelHeaderIconButton
                    expanded
                    title="Collapse"
                    onClick={onToggleExpand}
                  >
                    <CloseFullscreen />
                  </PanelHeaderIconButton>
                ) : (
                  <Mui.Tooltip arrow slotProps={tooltipStyleProps} title="Open full view">
                    <IconButton
                      onClick={onToggleExpand}
                      size="small"
                      sx={{
                        ...designShell.panelSoftIconButton,
                        color: designColors.slate500,
                      }}
                    >
                      <OpenFullViewIcon fontSize="small" />
                    </IconButton>
                  </Mui.Tooltip>
                )
              ) : null}
              {expanded ? (
                <PanelHeaderIconButton
                  expanded
                  title="Close"
                  onClick={onClose}
                >
                  <Close />
                </PanelHeaderIconButton>
              ) : (
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    ...designShell.panelSoftIconButton,
                    color: designColors.slate500,
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Mui.Stack>
          </Mui.Stack>
        </Box>
      </Paper>
      <Mui.Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            <Box>
              <GlobalFieldLabel helperText="Choose whether to apply a new break or cancel an existing one">
                Request Type
              </GlobalFieldLabel>
              <Controller
                name="requestMode"
                control={control}
                render={({ field }) => (
                  <Mui.Select
                    fullWidth
                    size="small"
                    value={field.value}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      if (!nextValue) return;
                      setValue("break_category", "OT Break");
                      setValue("startTime", null);
                      setValue("endTime", null);
                      setValue("break_to_cancel", []);
                      clearErrors(["startTime", "endTime", "break_to_cancel"]);
                      field.onChange(nextValue);
                    }}
                  >
                    <Mui.MenuItem value="cancel">Cancel Break</Mui.MenuItem>
                  </Mui.Select>
                )}
              />
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Select the employee for whom the Break or Cancel Break request is being created.">
                Employee name
              </GlobalFieldLabel>
              <CustomAutoCompleteWithLogo
                dataList={users?.map((obj) => ({
                  ...obj,
                  label: obj.employee_name,
                  image: obj?.image_url,
                }))}
                selectedValue={watch("selectedUser")}
                rules={{
                  ...register("user_name", {
                    required: "User name is required",
                  }),
                }}
                updateDataFunction={updateUserName}
                placeHolder="Select the name"
                errorMessage={
                  errors?.user_name?.message ? errors?.user_name?.message : ""
                }
                textBoxError={errors.user_name ? true : false}
                showAvathar={showAutoCompleteAvathar}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
              />
            </Box>
            <SelectedEmployeeHeader selectedUser={watch("selectedUser")} />
            <Box>
              <GlobalFieldLabel helperText="Select the attendance date to which the Break or Cancel Break request applies.">
                Date
              </GlobalFieldLabel>
              <CustomizedDatePicker
                name="date"
                control={control}
                required
                requiredMessage="Date is required"
                placeholder="Select date"
                disableFuture
                handleDisabledFutureDate={true}
              />
            </Box>

            {requestMode === "apply" ? (
              <Box>
                <GlobalFieldLabel helperText="Select when break should be considered.">
                  Break Category
                </GlobalFieldLabel>
                <Controller
                  name="break_category"
                  control={control}
                  defaultValue="OT Break"
                  render={({ field }) => (
                    <Mui.FormControl component="fieldset">
                      <Mui.RadioGroup row sx={{ gap: 2 }} {...field}>
                        <Mui.FormControlLabel
                          value="OT Break"
                          control={<Mui.Radio />}
                          label="OT Break"
                        />
                        <Mui.FormControlLabel
                          value="Shift Break"
                          control={<Mui.Radio />}
                          label="Shift Break"
                        />
                      </Mui.RadioGroup>
                    </Mui.FormControl>
                  )}
                />
                <BreakContextInfo />
              </Box>
            ) : (
              <Box>
                <GlobalFieldLabel helperText="Select one or more existing break entries to cancel">
                  Break to Cancel
                </GlobalFieldLabel>
                <input
                  type="hidden"
                  {...register("break_to_cancel", {
                    validate: (value) =>
                      requestMode !== "cancel" ||
                      (Array.isArray(value) && value.length > 0) ||
                      "Select at least one break to cancel",
                  })}
                />
                <Paper
                  elevation={0}
                  sx={{
                    mt: 1,
                    border: `1px solid ${themes?.miliedLightGray}`,
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Mui.Grid
                    container
                    sx={{
                      px: 1.5,
                      py: 1,
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <Mui.Grid item xs={1} />
                    <Mui.Grid item xs={3}>
                      <Typography variant="caption" color={designColors.slate600}>
                        Break Start
                      </Typography>
                    </Mui.Grid>
                    <Mui.Grid item xs={3}>
                      <Typography variant="caption" color={designColors.slate600}>
                        Break End
                      </Typography>
                    </Mui.Grid>
                    <Mui.Grid item xs={3}>
                      <Typography variant="caption" color={designColors.slate600}>
                        Duration
                      </Typography>
                    </Mui.Grid>
                    <Mui.Grid item xs={2}>
                      <Typography variant="caption" color={designColors.slate600}>
                        Type
                      </Typography>
                    </Mui.Grid>
                  </Mui.Grid>
                  {shouldFetchCancelBreakOptions && cancelBreakLoading ? (
                    [0, 1].map((row) => (
                      <Mui.Grid
                        container
                        key={`cancel-break-skeleton-${row}`}
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          alignItems: "center",
                          borderTop: `1px solid ${themes?.miliedLightGray}`,
                        }}
                      >
                        <Mui.Grid item xs={1}>
                          <Skeleton variant="rounded" width={18} height={18} />
                        </Mui.Grid>
                        {[3, 3, 3, 2].map((size, col) => (
                          <Mui.Grid item xs={size} key={col} sx={{ pr: 1 }}>
                            <Skeleton variant="text" width="70%" />
                          </Mui.Grid>
                        ))}
                      </Mui.Grid>
                    ))
                  ) : cancelBreakOptions.length ? (
                    cancelBreakOptions.map((item) => {
                      const selectedBreaksToCancel =
                        watch("break_to_cancel") || [];
                      const isChecked = selectedBreaksToCancel.some(
                        (entry) => entry?.id === item?.cancel_row_id,
                      );
                      return (
                        <Mui.Grid
                          container
                          key={item?.cancel_row_id}
                          sx={{
                            px: 1.5,
                            py: 1,
                            alignItems: "center",
                            borderTop: `1px solid ${themes?.miliedLightGray}`,
                          }}
                        >
                          <Mui.Grid item xs={1}>
                            <Mui.Checkbox
                              size="small"
                              checked={isChecked}
                              onChange={() => {
                                const next = isChecked
                                  ? selectedBreaksToCancel.filter(
                                      (entry) =>
                                        entry?.id !== item?.cancel_row_id,
                                    )
                                  : [
                                      ...selectedBreaksToCancel,
                                      {
                                        id: item?.cancel_row_id,
                                        type: item?.category_code,
                                      },
                                    ];
                                setValue("break_to_cancel", next);
                                clearErrors("break_to_cancel");
                              }}
                            />
                          </Mui.Grid>
                          <Mui.Grid item xs={3}>
                            <Typography sx={{ lineHeight: 1.3 }}>
                              {item?.start_time || item?.break_start_time
                                ? dayjs(
                                    item?.start_time || item?.break_start_time,
                                  ).format("HH:mm")
                                : "-"}
                            </Typography>
                          </Mui.Grid>
                          <Mui.Grid item xs={3}>
                            <Typography sx={{ lineHeight: 1.3 }}>
                              {item?.end_time || item?.break_end_time
                                ? dayjs(
                                    item?.end_time || item?.break_end_time,
                                  ).format("HH:mm")
                                : "-"}
                            </Typography>
                          </Mui.Grid>
                          <Mui.Grid item xs={3}>
                            <Typography sx={{ lineHeight: 1.3 }}>
                              {item?.duration || "-"}
                            </Typography>
                          </Mui.Grid>
                          <Mui.Grid item xs={2}>
                            <Typography sx={{ lineHeight: 1.3 }}>
                              {item?.category_label}
                            </Typography>
                          </Mui.Grid>
                        </Mui.Grid>
                      );
                    })
                  ) : (
                    <Typography
                      sx={{
                        p: 1.5,
                        fontSize: "0.9rem",
                        color: designColors.slate600,
                      }}
                    >
                      No breaks found for the selected date
                    </Typography>
                  )}
                </Paper>
                {errors?.break_to_cancel?.message ? (
                  <Typography
                    sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}
                  >
                    {errors.break_to_cancel.message}
                  </Typography>
                ) : null}
              </Box>
            )}

            {requestMode === "apply" ? (
              <Box>
                <GlobalFieldLabel helperText="Select the start time for the break period.">
                  Start Time
                </GlobalFieldLabel>
                <CustomTimePicker
                  name="startTime"
                  control={control}
                  errors={errors}
                  minTime={shiftMinTime}
                  maxTime={shiftMaxTime}
                  shouldDisableTime={disableBreakTime}
                  validateFn={(value) => {
                    if (!value || !dayjs(value).isValid()) {
                      return "Start time is required";
                    }
                    if (
                      breakCategory === "Shift Break" &&
                      !isTimeWithinShiftWindow(value)
                    ) {
                      return "Start time must fall within shift hours";
                    }
                    if (
                      breakCategory === "Shift Break" &&
                      breakEndTime &&
                      dayjs(breakEndTime).isValid()
                    ) {
                      const currentStart = getBreakDateTime(value);
                      const currentEnd = getBreakDateTime(breakEndTime);

                      if (!currentStart || !currentEnd) return true;

                      const hasOverlap = existingShiftBreaks.some((item) => {
                        const existingStart = dayjs(
                          item?.start_time || item?.break_start_time,
                        );
                        const existingEnd = dayjs(
                          item?.end_time || item?.break_end_time,
                        );
                        if (!existingStart.isValid() || !existingEnd.isValid()) {
                          return false;
                        }
                        return (
                          currentStart.isBefore(existingEnd) &&
                          currentEnd.isAfter(existingStart)
                        );
                      });

                      if (hasOverlap) {
                        return "Selected break time overlaps with an existing break";
                      }

                      if (
                        shiftDurationInMinutes !== null &&
                        currentEnd.diff(currentStart, "minute") >
                          shiftDurationInMinutes
                      ) {
                        return "Selected break duration cannot exceed shift hours";
                      }
                    }
                    if (
                      breakCategory === "OT Break" &&
                      breakEndTime &&
                      dayjs(breakEndTime).isValid()
                    ) {
                      const currentStart = getBreakDateTime(value);
                      const currentEnd = getBreakDateTime(breakEndTime);
                      const otBreakValidation = validateOtBreakRange(
                        currentStart,
                        currentEnd,
                      );
                      if (otBreakValidation) return otBreakValidation;
                    }
                    return true;
                  }}
                />
              </Box>
            ) : null}

            {requestMode === "apply" ? (
              <Box>
                <GlobalFieldLabel helperText="Select the end time for the break period.">
                  End Time
                </GlobalFieldLabel>
                <CustomTimePicker
                  name="endTime"
                  control={control}
                  errors={errors}
                  minTime={shiftMinTime}
                  maxTime={shiftMaxTime}
                  shouldDisableTime={disableBreakTime}
                  validateFn={(value) => {
                    const startTime = watch("startTime");

                    if (!value || !dayjs(value).isValid()) {
                      return "End time is required";
                    }

                    if (startTime) {
                      const currentStart = getBreakDateTime(startTime);
                      const currentEnd = getBreakDateTime(value);

                      if (
                        currentStart &&
                        currentEnd &&
                        currentEnd.isBefore(currentStart)
                      ) {
                        return "End time must be after start time";
                      }
                    }
                    if (
                      breakCategory === "Shift Break" &&
                      !isTimeWithinShiftWindow(value)
                    ) {
                      return "End time must fall within shift hours";
                    }
                    if (
                      breakCategory === "Shift Break" &&
                      startTime &&
                      dayjs(startTime).isValid()
                    ) {
                      const currentStart = getBreakDateTime(startTime);
                      const currentEnd = getBreakDateTime(value);
                      if (!currentStart || !currentEnd) return true;

                      const hasOverlap = existingShiftBreaks.some((item) => {
                        const existingStart = dayjs(
                          item?.start_time || item?.break_start_time,
                        );
                        const existingEnd = dayjs(
                          item?.end_time || item?.break_end_time,
                        );
                        if (!existingStart.isValid() || !existingEnd.isValid()) {
                          return false;
                        }
                        return (
                          currentStart.isBefore(existingEnd) &&
                          currentEnd.isAfter(existingStart)
                        );
                      });

                      if (hasOverlap) {
                        return "Selected break time overlaps with an existing break";
                      }

                      if (
                        shiftDurationInMinutes !== null &&
                        currentEnd.diff(currentStart, "minute") >
                          shiftDurationInMinutes
                      ) {
                        return "Selected break duration cannot exceed shift hours";
                      }
                    }
                    if (
                      breakCategory === "OT Break" &&
                      startTime &&
                      dayjs(startTime).isValid()
                    ) {
                      const currentStart = getBreakDateTime(startTime);
                      const currentEnd = getBreakDateTime(value);
                      const otBreakValidation = validateOtBreakRange(
                        currentStart,
                        currentEnd,
                      );
                      if (otBreakValidation) return otBreakValidation;
                    }

                    return true;
                  }}
                />
              </Box>
            ) : null}

            {requestMode === "apply" ? (
              <Box>
                <GlobalFieldLabel helperText="Displays the configured break duration.">
                  Duration
                </GlobalFieldLabel>
                <Box
                  sx={{
                    minHeight: 40,
                    border: `1px solid ${themes?.miliedLightGray}`,
                    borderRadius: 1,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography>{selectedBreakDuration}</Typography>
                </Box>
              </Box>
            ) : null}

            <Box>
              <GlobalFieldLabel helperText="Enter the reason for this request">
                Reason
              </GlobalFieldLabel>
              <CustomTextBox
                type="multiline"
                rules={{
                  ...register("reason", {
                    required: "Reason is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else if (value?.trim().length > 255 && value !== "") {
                          return "Maximum length is 255 characters";
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                selectedValue={watch("reason")}
                updateDataFunction={(event) => {
                  if (event.target.value.length <= 1024) {
                    setValue("reason", event.target.value);
                  }
                }}
                placeHolder="Enter the reason for this request"
                errorMessage={
                  errors?.reason?.message ? errors?.reason?.message : ""
                }
                textBoxError={errors?.reason ? true : false}
              />
            </Box>
          </Box>
        </Box>
        <Mui.Box
          sx={{
            px: 3,
            py: 1.25,
            borderTop: `1px solid ${designColors.slate200}`,
            backgroundColor: designColors.white,
          }}
        >
          <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <Mui.Tooltip title={applyDisabledReason} arrow disableHoverListener={!applyDisabledReason}>
              <Box component="span" sx={{ display: "inline-flex" }}>
                <PrimaryButton
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                  loading={Boolean(watch("button_loader"))}
                  disabled={Boolean(watch("button_loader")) || Boolean(applyDisabledReason)}
                  sx={{ minWidth: FORM_ACTION_BUTTON_WIDTH }}
                >
                  {hasApproverAccess ? "Approve" : "Apply"}
                </PrimaryButton>
              </Box>
            </Mui.Tooltip>
          </Mui.Stack>
        </Mui.Box>
      </Mui.Box>
    </Box>
  );

  if (expanded) {
    return createPortal(
      <Box
        sx={{
          position: "fixed",
          top: APP_BAR_OFFSET,
          left: `${contentLeft}px`,
          right: 0,
          bottom: 0,
          zIndex: 10,
          overflow: "hidden",
          backgroundColor: designColors.pageBg,
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ height: "100%", minHeight: "100%", width: "100%" }}>
          {panelContent}
        </Box>
      </Box>,
      document.body,
    );
  }

  return (
    <Mui.Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={panelDrawerSx()}
    >
      <Box sx={{ width: PANEL_DRAWER_WIDTH, maxWidth: "100%", height: "100%" }}>
        {panelContent}
      </Box>
    </Mui.Drawer>
  );
};
